import { put, select, takeEvery, takeLatest } from "redux-saga/effects"
import {
  axiosPut,
  delPollsAxios,
  getPollsAxios,
  post,
  postPollsAxios,
  putPollsAxios,
} from "helpers/api_helper"
import { getFormDataFromJSON, showToast } from "utils"
import {
  showSavingModal,
  hideSavingModal,
  setSelectedFeature,
  setIsOutdated,
  putMenuItemSuccess,
  hideDeleteFeatureModal,
} from "../actions"
import {
  addFormBuilderFailed,
  addFormBuilderSuccess,
  deleteFormBuilderFailed,
  deleteFormBuilderSuccess,
  getFormBuilderFailed,
  getFormBuilderListFailed,
  getFormBuilderListSuccess,
  getFormBuilderReplyFailed,
  getFormBuilderReplySuccess,
  getFormBuilderSuccess,
  hideDeleteFormBuilderModal,
  refundFormBuilderFailed,
  refundFormBuilderSuccess,
  updateFeatureRecipientFailed,
  updateFeatureRecipientSuccess,
  updateFormBuilderFailed,
  updateFormBuilderFeatureFailed,
  updateFormBuilderFeatureSuccess,
  updateFormBuilderSuccess,
} from "./actions"
import {
  ADD_FORM_BUILDER,
  DELETE_FORM_BUILDER,
  GET_FORM_BUILDER,
  GET_FORM_BUILDER_LIST,
  GET_FORM_BUILDER_REPLY,
  REFUND_FORM_BUILDER,
  UPDATE_FEATURE_RECIPIENT,
  UPDATE_FORM_BUILDER,
  UPDATE_FORM_BUILDER_FEATURE,
} from "./actionTypes"
import * as url from "helpers/url_helper"

function* addFormBuilder({ payload: { questions, params } }) {
  try {
    yield put(showSavingModal())
    const questionResponse = yield postPollsAxios("question", {
      questions,
    })
    const formBuilderResponse = yield postPollsAxios(
      "form-builder",
      getFormDataFromJSON({
        ...params,
        questions: questionResponse.map(x => x.id),
      })
    )
    const menuItem = yield axiosPut("/admin/menu-item", {
      entity_id: formBuilderResponse.id,
      menu_item_id: formBuilderResponse.menu_item_id,
    })
    yield put(putMenuItemSuccess(menuItem))
    yield put(setSelectedFeature(menuItem))
    yield put(addFormBuilderSuccess(formBuilderResponse))
    yield put(setIsOutdated(true))
    yield put(hideSavingModal())
  } catch (error) {
    yield put(addFormBuilderFailed(error))
  }
}

function* getFormBuilder({ payload: params }) {
  try {
    const response = yield getPollsAxios(`/form-builder/${params}`)
    const destroyed = yield select(state => state.FormBuilder.destroyed)
    if (!destroyed) {
      yield put(getFormBuilderSuccess(response))
    }
  } catch (error) {
    yield put(getFormBuilderFailed(error))
  }
}

function* updateFormBuilder({ payload: { questions, params, entity_id } }) {
  try {
    yield put(showSavingModal())
    const newQuestions = questions.filter(question => !question.id)
    let newQuestionsResponse = []
    if (newQuestions.length > 0) {
      newQuestionsResponse = yield postPollsAxios("question", {
        questions: [...newQuestions],
      })
    }
    const questionIds = newQuestionsResponse.map(question => question.id)
    for (let item of questions) {
      if (item.id) {
        const response = yield putPollsAxios(`/question/${item.id}`, item)
        questionIds.push(response.id)
      }
    }
    const formBuilderList = yield postPollsAxios(
      `/form-builder/${entity_id}`,
      getFormDataFromJSON({
        ...params,
        questions: questionIds,
      })
    )
    if (params.menu_feature === 1) {
      const formBuilderResponse = yield getPollsAxios(
        `/form-builder/${entity_id}`
      )
      yield put(getFormBuilderSuccess(formBuilderResponse))
    }
    yield put(updateFormBuilderSuccess(formBuilderList))
    yield put(hideSavingModal())
  } catch (error) {
    yield put(updateFormBuilderFailed(error))
  }
}

function* getFormBuilderList({ payload: params }) {
  try {
    const response = yield getPollsAxios("/form-builder", {
      params,
    })
    const destroyed = yield select(state => state.FormBuilder.listDestroyed)
    if (!destroyed) {
      yield put(getFormBuilderListSuccess(response))
    }
  } catch (error) {
    yield put(getFormBuilderListFailed(error))
  }
}

function* updateFormBuilderFeature({ payload: params }) {
  try {
    const response = yield postPollsAxios(
      `/form-builder/${params.featureId}`,
      getFormDataFromJSON(params.formBuilder)
    )
    yield put(setIsOutdated(true))
    yield put(updateFormBuilderFeatureSuccess(response))
  } catch (error) {
    yield put(updateFormBuilderFeatureFailed(error))
  }
}

function* deleteFormBuilder({ payload: params }) {
  try {
    const response = yield delPollsAxios(`/form-builder/${params.featureId}`, {
      menu_item_id: params.menu_item_id,
    })
    yield put(deleteFormBuilderSuccess(response))
    yield put(hideDeleteFormBuilderModal())
    yield put(hideDeleteFeatureModal())
  } catch (error) {
    yield put(hideDeleteFormBuilderModal())
    yield put(deleteFormBuilderFailed(error))
  }
}

function* updateFeatureRecipient({ payload: params }) {
  try {
    const response = yield post(url.FEATURE_RECIPIENTS_URL, params)
    yield put(updateFeatureRecipientSuccess(response))
    showToast("success", "Form Builder Updated", "Success")
  } catch (error) {
    yield put(updateFeatureRecipientFailed(error))
    showToast("error", error, "Error")
  }
}

function* getFormBuilderReply({ payload: params }) {
  try {
    const response = yield getPollsAxios(`/form-builder/${params?.id}/reply`)

    yield put(getFormBuilderReplySuccess(response))
  } catch (error) {
    yield put(getFormBuilderReplyFailed(error))
  }
}

function* refundFormBuilder({ payload: params }) {
  try {
    const response = yield post(
      `/admin/form-builder-completion/${params?.values?.form_builder_completion_id}/refund`,
      params?.values
    )
    yield put(refundFormBuilderSuccess(response))
    showToast("success", "Payment refunded", "Success")
    if (params?.callback) {
      params?.callback()
    }
  } catch (error) {
    yield put(refundFormBuilderFailed(error))
    showToast("error", error, "Error")
  }
}

function* formBuilderSaga() {
  yield takeEvery(ADD_FORM_BUILDER, addFormBuilder)
  yield takeLatest(GET_FORM_BUILDER, getFormBuilder)
  yield takeEvery(UPDATE_FORM_BUILDER, updateFormBuilder)
  yield takeEvery(GET_FORM_BUILDER_LIST, getFormBuilderList)
  yield takeEvery(UPDATE_FORM_BUILDER_FEATURE, updateFormBuilderFeature)
  yield takeEvery(DELETE_FORM_BUILDER, deleteFormBuilder)
  yield takeEvery(UPDATE_FEATURE_RECIPIENT, updateFeatureRecipient)
  yield takeEvery(GET_FORM_BUILDER_REPLY, getFormBuilderReply)
  yield takeEvery(REFUND_FORM_BUILDER, refundFormBuilder)
}

export default formBuilderSaga
